import i18n from "i18next";
import {
	CustomerOrderOfferDataGridApi,
	CustomerOrderOfferDataGridApi_CustomerOrderOfferDto,
} from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferDataGridApi.ts";
import {
	CustomerOrderOfferState,
	getCustomerOrderOfferStateLabels,
} from "src/api/generated/erp/db/types/enums/customerOrderOfferState.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid";
import { booleanColumn, dateColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel";
import { dataGridRowClassNames } from "src/components/common/dataGrid/styles/dataGridClassNames.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SingleCustomerOfferView } from "src/components/views/erp/sales/customerOrderOffer/SingleCustomerOfferView.tsx";
import { formatIsoString } from "src/utils/dayjsUtils.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesLeadOffersDataGridFetchParams {
	searchQuery: string;
	showOnlyDefaultSiteOffers: boolean;
	stateFilter: Array<CustomerOrderOfferState>;
	showDiscardedOffers: boolean;
	showOrderedOffers: boolean;
	showLostOffers: boolean;
	showCancelledOffers: boolean;
	salesLeadId: number;
}

export interface SalesLeadOffersDataGridProps {
	salesLeadId: number;
	refreshRef?: RefreshableElementRef<SalesLeadOffersDataGridFetchParams>;
}

export const SalesLeadOffersDataGrid = ({ salesLeadId, refreshRef }: SalesLeadOffersDataGridProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ServerSideDataGridModel
			fetchData={(params) => CustomerOrderOfferDataGridApi.getCustomerOrderOffers(params)}
			getDataModelResult={(response) => response}
			initialParams={{
				searchQuery: "",
				showOnlyDefaultSiteOffers: false,
				stateFilter: [],
				showDiscardedOffers: true,
				showOrderedOffers: true,
				showLostOffers: true,
				showCancelledOffers: true,
				salesLeadId,
			}}
			getRowId={(row) => row.customerOrderOfferId}
			gridId={"F0D6E8C2B5A14973"}
			refreshRef={refreshRef}
			usePagination={false}
			render={({ dataGridProps }) => (
				<FormCrudDataGrid<CustomerOrderOfferDataGridApi_CustomerOrderOfferDto>
					getRowClassNames={({ row }) => {
						if (!row.billingPlanIsComplete && row.customerOrderOfferState !== "INITIAL") {
							return dataGridRowClassNames.invalid;
						}
					}}
					getRowTooltip={(row) => (!row.billingPlanIsComplete ? i18n.t("billing_plan_invalid") : undefined)}
					columns={[
						integerColumn({
							field: "customerOrderOfferId",
							headerName: i18n.t("number_shortened"),
							width: 70,
							mobile: "header",
						}),
						textColumn({
							field: "siteName",
							headerName: i18n.t("site"),
							width: 100,
						}),
						textColumn({
							field: "customerName",
							headerName: i18n.t("customer"),
							width: 150,
							mobile: "header",
						}),
						textColumn({
							field: "orderReference",
							headerName: i18n.t("reference"),
							width: 150,
							mobile: "cell",
						}),
						textColumn({
							field: "customerOfferRequestNo",
							headerName: i18n.t("customer_offer_request_no"),
							width: 150,
						}),
						enumColumn({
							field: "customerOrderOfferState",
							headerName: i18n.t("state"),
							enumLabels: getCustomerOrderOfferStateLabels(),
							mobile: "cell",
						}),
						booleanColumn({
							field: "frozen",
							headerName: i18n.t("frozen"),
						}),
						dateColumn({
							field: "plannedDeliveryDate",
							headerName: i18n.t("planned_delivery_date"),
							width: 150,
						}),
						{
							field: "validUntilOrValidForDays",
							headerName: i18n.t("valid_until"),
							filterable: false,
							sortable: false,
							valueGetter: (_, row) =>
								row.validUntil != null ?
									i18n.t("valid_until_to", { date: formatIsoString(row.validUntil) })
								:	i18n.t("valid_for_days_param_days", { days: row.validForDays }),
							mobile: "cell",
						},
						textColumn({
							field: "responsiblePersonName",
							headerName: i18n.t("responsible_person"),
						}),
						textColumn({
							field: "salespersonName",
							headerName: i18n.t("salesperson"),
							mobile: "cell",
						}),
						dateColumn({
							field: "createdAt",
							headerName: i18n.t("created_at"),
						}),
						dateColumn({
							field: "offeredAt",
							headerName: i18n.t("offered_at"),
						}),
						dateColumn({
							field: "closedAt",
							headerName: i18n.t("closed_at"),
						}),
					]}
					openRow={(row) => {
						openDialog(() => ({
							title: i18n.t("offer"),
							content: <SingleCustomerOfferView customerOrderOfferId={row.customerOrderOfferId} />,
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customerOrderOffer.dialog"),
								title: i18n.t("offer"),
								pathSegments: [`${i18n.t("offer")} ${row.customerOrderOfferId}`],
								data: {
									customerOrderOfferId: row.customerOrderOfferId,
									salesLeadId,
								},
							},
						}));
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.crm.salesLeadOffers.grid"),
						data: { salesLeadId },
						selectedRowPathSegment: (row) => `${i18n.t("offer")} ${row.customerOrderOfferId}`,
						selectedRowsPathSegment: (count) => i18n.t("customer_offers_selected_count", { count }),
						rowFields: ["customerOrderOfferId", "salesLeadId", "customerId", "customerOrderId", "orderReference"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
